import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import apiFactory from "../../api";
import { socketStatus$ } from "../../api/webSocketRx";
import { eventBusType } from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";
import { downloadEvent$ } from "../../api/eventBus";

export const DOWNLOAD_STATUS = {
  IDLE: "idle",
  DOWNLOADING: "downloading",
  PAUSED: "paused",
  SUCCESS: "success",
  ERROR: "error",
  CANCEL: "cancel",
};

const getHeader = (headers, key) => {
  if (!headers) return undefined;

  if (typeof headers.get === "function") {
    return headers.get(key) || headers.get(key.toLowerCase());
  }

  return headers[key] || headers[key.toLowerCase()];
};

const getTotalSizeFromHeaders = (headers) => {
  const contentRange = getHeader(headers, "content-range");

  if (contentRange) {
    const match = /bytes \d+-\d+\/(\d+)/.exec(contentRange);
    if (match?.[1]) return parseInt(match[1], 10);
  }

  const contentLength = getHeader(headers, "content-length");
  if (contentLength) return parseInt(contentLength, 10);

  return 0;
};

const getFilenameFromHeaders = (headers, defaultName = "download") => {
  const disposition = getHeader(headers, "content-disposition");

  if (!disposition) return defaultName;

  const utf8Match = /filename\*=UTF-8''([^;\n]*)/i.exec(disposition);

  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/['"]/g, ""));
  }

  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
  const matches = filenameRegex.exec(disposition);

  if (matches?.[1]) {
    return matches[1].replace(/['"]/g, "").trim();
  }

  return defaultName;
};

const triggerBrowserDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const parseJsonBlob = async (blob) => {
  const text = await blob.text();
  return JSON.parse(text);
};

const DownloadContext = createContext(null);

/* downloads struture
 * 1. path
 *
 * */
export const DownloadProvider = ({ children }) => {
  const { user } = useInfoUser();
  const [downloads, setDownloads] = useState({});

  const updateDownload = useCallback((resourceId, updater) => {
    setDownloads((prev) => {
      const current = prev[resourceId] || {};

      const nextValue =
        typeof updater === "function"
          ? updater(current)
          : {
              ...current,
              ...updater,
            };

      return {
        ...prev,
        [resourceId]: nextValue,
      };
    });
  }, []);

  const getDownload = useCallback(
    (resourceId) => downloads[resourceId],
    [downloads],
  );

  const initDownload = useCallback(
    (resourceId, customFileName) => {
      const download = {
        status: DOWNLOAD_STATUS.IDLE,
        progress: 0,
        error: null,
        blob: null,
        fileUrl: null,
        controller: new AbortController(),
        partialBlob: [],
        receivedLength: 0,
        totalSize: 0,
        currentChunkProgress: 0,
        fileName: customFileName || "download",
      };

      updateDownload(resourceId, download);

      return download;
    },
    [updateDownload],
  );

  /**
   * 1. ignore file with an empty MinIO URL
   * 2. ignore if the URL is already downloading (can be resumed later)
   */

  const startDownload = async (
    path,
    fileName,
    receivedLength = 0,
    isSave = true,
  ) => {
    if (!path) return;

    downloadEvent$.next({
      type: eventBusType.STARTING,
      payload: {
        path,
        fileName,
        receivedLength,
        isSave,
      },
    });
  };

  const continueDownload = async (
    path,
    customFileName,
    receivedLength,
    isSave = true,
    signal,
  ) => {
    try {
      const startByte = receivedLength || 0;
      if (!/^https?:\/\//i.test(path)) {
        const response = await apiFactory.resourceApi.downloadFile(
          path,
          undefined,
          signal,
          startByte > 0 ? { Range: `bytes=${startByte}-` } : undefined,
        );

        downloadEvent$.next({
          type: eventBusType.DOWNLOADING,
          payload: {
            path,
            blob: response.data,
            totalSize: getTotalSizeFromHeaders(response.headers),
            fileName:
              customFileName ||
              getFilenameFromHeaders(response.headers, "download"),
            isSave,
          },
        });
        return;
      }

      const response = await fetch(path, {
        signal,
        headers: startByte > 0 ? { Range: `bytes=${startByte}-` } : undefined,
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const totalSize = getTotalSizeFromHeaders(response.headers);
      const fileName =
        customFileName || getFilenameFromHeaders(response.headers, "download");
      const reader = response.body?.getReader();

      if (!reader) {
        throw new Error("Download stream is not available");
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        setDownloads((prev) => {
          const current = prev[path];
          if (!current || current.status === DOWNLOAD_STATUS.PAUSED) {
            return prev;
          }

          const chunk = new Blob([value]);
          const nextReceivedLength = current.receivedLength + value.length;
          const nextTotalSize = totalSize || current.totalSize;

          return {
            ...prev,
            [path]: {
              ...current,
              partialBlob: [...current.partialBlob, chunk],
              receivedLength: nextReceivedLength,
              totalSize: nextTotalSize,
              fileName,
              isSave,
              progress: nextTotalSize
                ? Math.min(
                    100,
                    Math.round((nextReceivedLength * 100) / nextTotalSize),
                  )
                : 0,
            },
          };
        });
      }

      setDownloads((prev) => {
        const current = prev[path];
        if (!current || current.status === DOWNLOAD_STATUS.PAUSED) {
          return prev;
        }

        const finalBlob = new Blob(current.partialBlob);
        if (isSave) {
          triggerBrowserDownload(finalBlob, fileName);
          const next = { ...prev };
          delete next[path];
          return next;
        }

        return {
          ...prev,
          [path]: {
            ...current,
            status: DOWNLOAD_STATUS.SUCCESS,
            progress: 100,
          },
        };
      });
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") return;

      setDownloads((prev) => ({
        ...prev,
        [path]: {
          ...prev[path],
          status: DOWNLOAD_STATUS.ERROR,
          error: error?.message || "Download failed",
        },
      }));
    }
  };

  const startDownloadCompress = useCallback(
    async (resourceIds, customFileName) => {
      const downloadId = Array.isArray(resourceIds)
        ? resourceIds.join(",")
        : resourceIds;

      const currentDownload = initDownload(downloadId, customFileName);

      updateDownload(downloadId, {
        status: DOWNLOAD_STATUS.DOWNLOADING,
        progress: 0,
        error: null,
        blob: null,
        fileUrl: null,
      });

      try {
        const handleProgress = (progressEvent) => {
          const loaded = progressEvent.loaded ?? 0;
          const total = progressEvent.total ?? currentDownload.totalSize ?? 0;

          if (total > 0) {
            updateDownload(downloadId, {
              progress: Math.round((loaded * 100) / total),
            });
          }
        };

        const response = await apiFactory.resourceApi.downloadCompressFile(
          resourceIds,
          handleProgress,
          currentDownload.controller.signal,
        );

        const contentType = (
          getHeader(response.headers, "content-type") || ""
        ).toLowerCase();

        if (contentType.includes("application/json")) {
          const errorObject = await parseJsonBlob(response.data);

          updateDownload(downloadId, {
            status: DOWNLOAD_STATUS.ERROR,
            progress: 0,
            error: errorObject?.message || "Download failed",
          });

          return;
        }

        const blobData =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
                type: contentType || "application/zip",
              });

        const oldDownload = getDownload(downloadId);

        if (oldDownload?.fileUrl) {
          URL.revokeObjectURL(oldDownload.fileUrl);
        }

        const fileUrl = URL.createObjectURL(blobData);

        updateDownload(downloadId, {
          status: DOWNLOAD_STATUS.SUCCESS,
          progress: 100,
          blob: blobData,
          fileUrl,
        });

        const finalFileName =
          customFileName ||
          getFilenameFromHeaders(response.headers, "compressed_files.zip");

        triggerBrowserDownload(blobData, finalFileName);
      } catch (err) {
        if (currentDownload.controller.signal.aborted) return;

        updateDownload(downloadId, {
          status: DOWNLOAD_STATUS.ERROR,
          progress: 0,
          error: err?.message || "Download failed",
        });
      }
    },
    [getDownload, initDownload, updateDownload],
  );

  const pauseDownload = (path) => {
    setDownloads((prev) => {
      const current = prev[path];

      if (current?.controller) {
        current.controller.abort();
      }

      const next = { ...prev };
      if (next[path]) {
        next[path] = {
          ...next[path],
          status: DOWNLOAD_STATUS.PAUSED,
        };
      }
      return next;
    });
  };

  const resumeDownload = async (path) => {
    setDownloads((prev) => {
      const current = prev[path];

      if (current?.controller) {
        current.controller.abort();
      }

      const controller = new AbortController();
      const next = { ...prev };
      if (next[path]) {
        next[path] = {
          ...next[path],
          controller,
          status: DOWNLOAD_STATUS.DOWNLOADING,
        };
      }

      continueDownload(
        path,
        current?.fileName,
        current?.receivedLength ?? 0,
        current?.isSave,
        controller.signal,
      );
      return next;
    });
  };

  const cancelDownload = (path) => {
    setDownloads((prev) => {
      const current = prev[path];

      if (current?.controller) {
        current.controller.abort();
      }

      if (current?.fileUrl) {
        URL.revokeObjectURL(current.fileUrl);
      }

      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  useEffect(() => {
    const sub = socketStatus$.subscribe((eventData) => {
      if (!eventData) return;

      /**
       * after reconnect -> have to continue downloading if there are any downloading file (except paused or cancel)
       */
      if (eventData?.status === "CONNECTED") {
        setDownloads((prev) => {
          const next = { ...prev };
          Object.entries(next).forEach(([resourceId, download]) => {
            if (
              download.status === DOWNLOAD_STATUS.DOWNLOADING ||
              download.status === DOWNLOAD_STATUS.IDLE
            ) {
              next[resourceId] = {
                ...download,
                status: DOWNLOAD_STATUS.DOWNLOADING,
              };
            }
          });

          Object.entries(next).forEach(([resourceId, download]) => {
            continueDownload(resourceId, null, download.receivedLength ?? 0);
          });

          return next;
        });
      }
    });

    return () => sub.unsubscribe();
  }, [user?.userId]);

  useEffect(() => {
    const sub = downloadEvent$.subscribe((event) => {
      const payload = event?.payload;
      const path = payload?.path ?? payload?.resourceId;

      if (event?.type === eventBusType.STARTING) {
        setDownloads((prev) => {
          const current = prev?.[path];

          if (current) {
            return prev;
          } else {
              const controller = new AbortController();

            continueDownload(
              path,
              payload?.fileName,
              payload?.receivedLength,
              payload?.isSave,
                controller.signal,
            );

            return {
              ...prev,
              [path]: {
                controller,
                status: DOWNLOAD_STATUS.DOWNLOADING,
                partialBlob: [],
                receivedLength: payload?.receivedLength || 0,
                totalSize: 0,
                progress:
                  1000 > 0
                    ? Math.min(
                        100,
                        Math.round((payload?.receivedLength * 100) / 1000),
                      )
                    : 0,
              },
            };
          }
        });
      }

      if (event?.type === eventBusType.DOWNLOADING) {
        setDownloads((prev) => {
          const current = prev?.[path];
          // || {
          //   partialBlob: [],
          //   receivedLength: 0,
          // };

          if (!current) return prev;

          if (payload?.blob) {
            current.partialBlob.push(payload.blob);
            current.receivedLength += payload.blob.size || 0;
            current.totalSize = payload.totalSize || current.totalSize;
            current.fileName = payload.fileName || current.fileName;
            current.isSave = payload.isSave;
          }

          if (current?.status === DOWNLOAD_STATUS.PAUSED) {
            return prev;
          }

          if (current?.status === DOWNLOAD_STATUS.CANCEL) {
            const next = { ...prev };
            delete next[path];
            return next;
          }

          if (current.receivedLength < current.totalSize) {
            continueDownload(
              path,
              payload?.fileName,
              current.receivedLength,
              current.isSave,
              current.controller?.signal,
            );

            return {
              ...prev,
              [path]: {
                controller: current.controller,
                status: DOWNLOAD_STATUS.DOWNLOADING,
                partialBlob: current.partialBlob,
                receivedLength: current.receivedLength,
                totalSize: current.totalSize,
                currentChunkProgress: 0,
                fileName: current.fileName,
                progress:
                  current.totalSize > 0
                    ? Math.min(
                        100,
                        Math.round(
                          (current.receivedLength * 100) / current.totalSize,
                        ),
                      )
                    : 0,
                isSave: current.isSave,
              },
            };
          } else {
            const finalBlob = new Blob(current.partialBlob, {
              type: current.partialBlob[0]?.type,
            });

            if (current.isSave) {
              triggerBrowserDownload(finalBlob, current.fileName);
              /** after download is complete -> delete from downloads */
              const next = { ...prev };
              delete next[path];
              return next;
            } else {
              return {
                ...prev,
                [path]: {
                  ...current,
                  status: DOWNLOAD_STATUS.SUCCESS,
                },
              };
            }
          }
        });
      }

      if (event?.type === eventBusType.ERROR) {
        setDownloads((prev) => {
          const current = prev?.[path] || {
            partialBlob: [],
            receivedLength: 0,
          };

          if (current?.status === DOWNLOAD_STATUS.CANCEL) {
            const next = { ...prev };
            delete next[path];
            return next;
          }

          return prev;
        });
      }
    });

    return () => sub.unsubscribe();
  }, [user?.userId]);

  return (
    <DownloadContext.Provider
      value={{
        downloads,
        startDownload,
        startDownloadCompress,
        pauseDownload,
        resumeDownload,
        cancelDownload,
        DOWNLOAD_STATUS,
      }}
    >
      {children}
    </DownloadContext.Provider>
  );
};

export const useGlobalDownloader = () => {
  const context = useContext(DownloadContext);

  if (!context) {
    throw new Error(
      "useGlobalDownloader must be used within a DownloadProvider",
    );
  }

  return context;
};
